import { apiClient } from './axios';

// ============================================
// Types
// ============================================

export interface PageViewStats {
  summary: {
    totalViews: number;
    uniqueVisitors: number;
    authenticatedViews: number;
    guestViews: number;
    period: string;
  };
  pageStats: {
    path: string;
    views: number;
  }[];
  dailyTrend: {
    date: string;
    views: number;
  }[];
}

export interface RealtimePageViews {
  activeVisitors: number;
  recentViews: number;
  views: {
    path: string;
    viewedAt: string;
    isAuthenticated: boolean;
  }[];
}

export interface TopPage {
  rank: number;
  path: string;
  views: number;
}

export interface PageViewLog {
  id: string;
  path: string;
  ipHash: string;
  isAuthenticated: boolean;
  user: {
    id: string;
    username: string;
    email: string;
  } | null;
  viewedAt: string;
  referrer: string | null;
}

// ============================================
// Public API (인증 불필요)
// ============================================

/**
 * 페이지 조회수 기록
 */
export async function recordPageView(path: string, referrer?: string): Promise<void> {
  try {
    await apiClient.post('/pageviews', {
      path,
      referrer: referrer || document.referrer || null,
    });
  } catch (error) {
    // 조회수 기록 실패는 조용히 무시 (사용자 경험에 영향 없음)
    console.debug('PageView recording failed:', error);
  }
}

// ============================================
// Admin API (인증 필요)
// ============================================

/**
 * 페이지 조회수 통계 조회 (관리자용)
 */
export async function getPageViewStats(params?: {
  days?: number;
  path?: string;
}): Promise<PageViewStats> {
  const response = await apiClient.get('/pageviews/admin/stats', { params });
  return response.data.data;
}

/**
 * 실시간 조회 현황 (관리자용)
 */
export async function getRealtimePageViews(): Promise<RealtimePageViews> {
  const response = await apiClient.get('/pageviews/admin/realtime');
  return response.data.data;
}

/**
 * 인기 페이지 목록 (관리자용)
 */
export async function getTopPages(params?: {
  days?: number;
  limit?: number;
}): Promise<TopPage[]> {
  const response = await apiClient.get('/pageviews/admin/top-pages', { params });
  return response.data.data;
}

/**
 * 상세 접속 로그 (관리자용)
 */
export async function getPageViewLogs(params?: {
  limit?: number;
  offset?: number;
  path?: string;
}): Promise<{ data: PageViewLog[]; meta: { total: number; hasMore: boolean } }> {
  const response = await apiClient.get('/pageviews/admin/logs', { params });
  return response.data;
}
